import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { HardDrive, ImageOff, Loader2, Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const mb = (n) => `${Number(n || 0).toLocaleString('sv-SE')} MB`;

export const DbSizes = ({ token, t }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { setS((await axios.get(`${API_URL}/api/admin/db-cleanup/sizes`, { headers: { Authorization: `Bearer ${token}` } })).data); }
    catch { toast.error('Error'); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border bg-white p-4 space-y-3" data-testid="db-sizes">
      <div className="flex items-center gap-2">
        <HardDrive className="w-5 h-5 text-slate-600" />
        <h3 className="font-bold flex-1">{t('الحجم الحقيقي للبيانات', 'Verklig datastorlek')}</h3>
        <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="db-sizes-load">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('عرض', 'Visa')}
        </Button>
      </div>
      {s && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            <div className="rounded-lg bg-slate-50 p-2"><p className="text-xs text-slate-500">{t('البيانات الحقيقية', 'Data')}</p><p className="font-bold" data-testid="db-size-data">{mb(s.data_mb)}</p></div>
            <div className="rounded-lg bg-slate-50 p-2"><p className="text-xs text-slate-500">{t('المساحة على القرص', 'På disk')}</p><p className="font-bold" data-testid="db-size-storage">{mb(s.storage_mb)}</p></div>
            <div className="rounded-lg bg-emerald-50 p-2"><p className="text-xs text-emerald-700">{t('فارغة ومحجوزة للاستخدام', 'Ledigt, återanvänds')}</p><p className="font-bold text-emerald-700" data-testid="db-size-free">{mb(s.free_mb)}</p></div>
            <div className="rounded-lg bg-slate-50 p-2"><p className="text-xs text-slate-500">{t('الفهارس', 'Index')}</p><p className="font-bold">{mb(s.index_mb)}</p></div>
          </div>
          <p className="text-xs text-slate-500">{t('بعد الحذف تتحول المساحة إلى «فارغة» ويعيد MongoDB استخدامها للبيانات الجديدة، لذلك قد لا ينزل رقم القرص فوراً.', 'Efter radering blir utrymmet "ledigt" och återanvänds av MongoDB, därför sjunker diskstorleken inte direkt.')}</p>
          <ul className="text-xs divide-y" data-testid="db-sizes-list">
            {s.collections.map((c) => (
              <li key={c.name} className="flex justify-between py-1 gap-2"><span className="font-mono break-all">{c.name}</span><span className="tabular-nums whitespace-nowrap">{mb(c.storage_mb)}</span></li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export const OrphanFiles = ({ token, t }) => {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const scan = async () => {
    setBusy(true);
    try { setP((await axios.get(`${API_URL}/api/admin/db-cleanup/orphan-files`, { headers, timeout: 120000 })).data); }
    catch { toast.error('Error'); } finally { setBusy(false); }
  };
  const del = async () => {
    const word = t('احذف', 'RADERA');
    if ((window.prompt(t(`سيتم حذف ${p.count} صورة (${mb(p.mb)}) لا يستخدمها أي سؤال. صور الإشارات وقسم الأسئلة الجديد لا تُلمس. اكتب "احذف" للتأكيد:`,
      `${p.count} bilder (${mb(p.mb)}) som ingen fråga använder raderas. Skyltbilder och nya frågesektionen rörs inte. Skriv "RADERA":`)) || '').trim() !== word) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/db-cleanup/orphan-files`, { confirm: 'DELETE' }, { headers, timeout: 180000 });
      toast.success(t(`تم حذف ${data.deleted} صورة`, `${data.deleted} bilder raderade`));
      setP(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border bg-white p-4 space-y-3" data-testid="orphan-files">
      <div className="flex flex-wrap items-center gap-2">
        <ImageOff className="w-5 h-5 text-slate-600" />
        <h3 className="font-bold flex-1">{t('صور أسئلة قديمة غير مستخدمة', 'Oanvända gamla frågebilder')}</h3>
        <Button size="sm" variant="outline" onClick={scan} disabled={busy} data-testid="orphan-files-scan">
          {busy && !p ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}<span className="ms-1">{t('فحص', 'Sök')}</span>
        </Button>
        {p && p.count > 0 && (
          <Button size="sm" onClick={del} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="orphan-files-delete">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}<span className="ms-1">{t('حذف', 'Radera')}</span>
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-500">{t('يفحص صور الأسئلة فقط. صور إشارات المرور وصور قسم الأسئلة الجديد وأي صورة مستخدمة في مكان آخر لا تُحذف أبداً. النسخ الاحتياطية وسجل التعديلات لا تُعتبر استخداماً. قد يستغرق الفحص دقيقة.', 'Endast frågebilder. Skyltbilder, nya frågesektionens bilder och bilder som används någon annanstans raderas aldrig. Säkerhetskopior och redigeringshistorik räknas inte som användning. Sökningen kan ta en minut.')}</p>
      {p && (
        <div data-testid="orphan-files-result">
          <p className="text-sm font-semibold">{t(`${p.count} صورة غير مستخدمة · ${mb(p.mb)}`, `${p.count} oanvända bilder · ${mb(p.mb)}`)}</p>
          {p.files.length > 0 && (
            <ul className="mt-2 max-h-48 overflow-auto text-xs font-mono divide-y">
              {p.files.map((f) => <li key={f.filename} className="py-1 flex justify-between gap-2"><span className="break-all">{f.filename}</span><span>{f.kb} KB</span></li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
